import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pause, Play, RefreshCw } from "lucide-react";
import type { ActionAvailability } from "@amber/contracts";
import { Button, Callout, ReasonDialog, formatCost, formatDateTime, useCsrf } from "@amber/ui";
import type { SourceBulkAction, SourceBulkPreview, SourceRow } from "../../contracts/index";
import { AFTER_PLAN_CHANGE, ON_PLAN_CONFLICT, sourceBulkPreviewQuery, sourceCommands } from "../queries";
import { bulkCounts, type SourceSelection } from "./bulk";

const NONE: Record<SourceBulkAction, string> = {
  scan: "None of the selected sources can be scanned now: paused, not in any plan, not collected yet or already scanning",
  pause: "None of the selected sources can be paused",
  resume: "None of the selected sources is paused on its own",
};

const plural = (n: number, one: string, many = `${one}s`) => `${String(n)} ${n === 1 ? one : many}`;

export function SourcesBulkBar({
  rows,
  selection,
  actions,
  onOpen,
}: {
  rows: readonly SourceRow[];
  selection: SourceSelection;
  actions: { scan_now: ActionAvailability; pause: ActionAvailability };
  onOpen: (action: SourceBulkAction) => void;
}) {
  const n = selection.picked.size;
  if (n === 0) return null;
  const counts = bulkCounts(rows, selection.picked);
  const shown = new Set<string>(rows.map((r) => r.id));
  const hidden = [...selection.picked].filter((id) => !shown.has(id)).length;
  const button = (action: SourceBulkAction, icon: ReactNode, label: string, role: ActionAvailability) => {
    const count = counts[action];
    const reason = role.enabled ? (count === 0 && hidden === 0 ? NONE[action] : null) : role.reason;
    return (
      <Button
        size="sm"
        disabledReason={reason}
        onClick={() => {
          onOpen(action);
        }}
      >
        {icon}
        {label}
        {count < n ? (
          <span className="font-normal text-muted">
            {" "}
            ({count} of {n})
          </span>
        ) : null}
      </Button>
    );
  };
  return (
    <div
      role="region"
      aria-label="Selection"
      className="mx-4 mb-2 flex flex-wrap items-center gap-2.5 rounded-xl border border-link/35 bg-accent-soft py-1.5 pr-2 pl-3 text-md max-compact:mx-2"
    >
      <span>
        <b className="font-semibold">{n} selected</b>
        {hidden > 0 ? <span className="text-muted"> · {hidden} hidden by filters</span> : null}
      </span>
      <span className="ml-auto flex flex-wrap items-center gap-1.5">
        {button("scan", <RefreshCw aria-hidden="true" />, "Scan now…", actions.scan_now)}
        {button("pause", <Pause aria-hidden="true" />, "Pause…", actions.pause)}
        {button("resume", <Play aria-hidden="true" />, "Resume…", actions.pause)}
        <Button size="sm" variant="ghost" onClick={selection.clear}>
          Clear selection
        </Button>
      </span>
    </div>
  );
}

function titleOf(action: SourceBulkAction, included: number, selected: number): string {
  const which = included < selected ? `${String(included)} of ${String(selected)} selected` : String(included);
  const noun = selected === 1 ? "source" : "sources";
  if (action === "scan") return `Scan ${which} ${noun} now`;
  return `${action === "pause" ? "Pause" : "Resume"} ${which} ${noun}`;
}

function confirmOf(action: SourceBulkAction, n: number): string {
  if (action === "scan") return n === 1 ? "Queue 1 scan" : `Queue ${String(n)} scans`;
  return `${action === "pause" ? "Pause" : "Resume"} ${plural(n, "source")}`;
}

function successOf(action: SourceBulkAction, n: number): string {
  if (action === "scan") return `Scan queued for ${plural(n, "source")}`;
  if (action === "pause") return `Paused ${plural(n, "source")}. Nothing was deleted`;
  return `Resumed ${plural(n, "source")}`;
}

function Facts({ items }: { items: readonly [string, ReactNode][] }) {
  return (
    <dl className="mt-3 grid grid-cols-3 gap-2">
      {items.map(([label, value]) => (
        <div key={label} className="rounded-lg border border-divider bg-surface px-2.5 py-1.5">
          <dt className="text-sm text-muted">{label}</dt>
          <dd className="m-0 font-strong text-fg">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Intro({ preview }: { preview: SourceBulkPreview }) {
  const people = preview.people.length > 0 ? preview.people.join(", ") : "shared-source plans";
  const n = preview.included.length;
  switch (preview.action) {
    case "scan":
      return (
        <>
          Queues an immediate scan of each source outside its cadence, for <b>{people}</b>. New items go through the
          pipeline as usual; scheduled scans stay as they are.
          <Facts
            items={[
              ["Scans", n],
              ["People", preview.people.length],
              ["Estimated cost", formatCost(preview.estimate.amount_decimal)],
            ]}
          />
          <span className="mt-1 block text-sm text-muted">{preview.note}.</span>
        </>
      );
    case "pause":
      return (
        <>
          New items stop being found for <b>{people}</b> from these sources until someone resumes them. There is no
          automatic end. Nothing already collected is deleted; on resume the paused time becomes a gap.
          <Facts
            items={[
              ["Sources", n],
              ["People", preview.people.length],
              ["Scans skipped", `${String(Math.round(preview.scans_per_day))} a day`],
            ]}
          />
        </>
      );
    case "resume":
      return (
        <>
          Scans start again for <b>{people}</b> at each source&apos;s cadence. Each paused interval becomes a gap “Not
          scanned while paused”.
        </>
      );
  }
}

function SourceList({ preview }: { preview: SourceBulkPreview }) {
  const shared = preview.included.filter((s) => s.people.length > 1);
  return (
    <>
      <ul
        aria-label={`Sources to ${preview.action}`}
        className="mt-3 mb-0 max-h-40 list-disc overflow-auto pl-5 text-md"
      >
        {preview.included.map((s) => (
          <li key={s.id}>
            <b className="font-strong">{s.title}</b>
            {s.people.length > 0 ? <span className="text-muted"> · {s.people.join(", ")}</span> : null}
            {preview.action === "resume" && s.paused ? (
              <span className="text-muted">
                {" "}
                · paused since {formatDateTime(s.paused.at)} by {s.paused.by}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {preview.action === "pause" && shared.length > 0 ? (
        <Callout tone="waiting" className="mt-3">
          {shared.map((s) => `${s.title} is shared: ${s.people.join(", ")} depend on it.`).join(" ")} Pausing stops it
          for all of them.
        </Callout>
      ) : null}
      {preview.skipped.length > 0 ? (
        <>
          <p className="mt-3 mb-1 text-md font-strong">Skipped</p>
          <ul aria-label="Skipped" className="m-0 max-h-32 list-disc overflow-auto pl-5 text-md text-muted">
            {preview.skipped.map((s) => (
              <li key={s.id}>
                {s.title}: {s.reason}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </>
  );
}

function CatchUp({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label className="mt-3 flex items-start gap-2 text-ui">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={checked}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
      />
      <span>
        <b className="font-strong">Catch up on the paused interval</b>
        <small className="block text-sm text-muted">
          {checked
            ? "Queues a catch-up scan for each source behind fresh monitoring, at the usual cost per new item; each gap stays open until its scan has read the whole interval."
            : "Without catch-up each gap needs someone and a task is created."}
        </small>
      </span>
    </label>
  );
}

export function SourceBulkDialog({
  action,
  ids,
  onClose,
  onDone,
}: {
  action: SourceBulkAction;
  ids: readonly string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const csrf = useCsrf();
  const preview = useQuery(sourceBulkPreviewQuery({ action, source_ids: [...ids] }, csrf()));
  const [catchUp, setCatchUp] = useState(true);
  const data = preview.data;
  const n = data?.included.length ?? 0;
  return (
    <ReasonDialog
      title={data ? titleOf(action, n, ids.length) : "Preparing the preview…"}
      confirm={confirmOf(action, n)}
      success={successOf(action, n)}
      wide
      ready={data !== undefined}
      disabledReason={data && n === 0 ? NONE[action] : null}
      onClose={onClose}
      onDone={onDone}
      invalidate={AFTER_PLAN_CHANGE}
      invalidateOnConflict={ON_PLAN_CONFLICT}
      run={(reason, token) =>
        sourceCommands.bulk(
          {
            action,
            sources: (data?.included ?? []).map((s) => ({ id: s.id, base_revision: s.revision })),
            reason,
            catch_up: catchUp,
          },
          token,
        )
      }
      extra={
        data ? (
          <>
            <div className="text-ui">
              <Intro preview={data} />
            </div>
            <SourceList preview={data} />
            {action === "resume" && n > 0 ? <CatchUp checked={catchUp} onChange={setCatchUp} /> : null}
          </>
        ) : (
          <p className="m-0 mt-3 text-muted">Preparing the preview…</p>
        )
      }
    />
  );
}
